import { create } from "zustand";
import { persist } from "zustand/middleware";

// Import pure utility functions (no circular dependencies)
import {
  parseJwt,
  setRefreshTokenCookie,
  clearRefreshTokenCookie,
  getRefreshTokenCookie,
} from "../services/utils/tokenUtils";

/**
 * Extracts and formats a display name from an email address
 * @param {string} email -
 * @returns {string|null}
 */
const extractDisplayNameFromEmail = (email) => {
  if (!email || typeof email !== "string") return null;

  const emailPart = email.split("@")[0];
  const namePart = emailPart.split(".")[0];

  if (!namePart) return null;

  return namePart.charAt(0).toUpperCase() + namePart.slice(1).toLowerCase();
};

// Auth store with custom storage strategy
const useAuthStore = create(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,

      // Computed user data (stored as regular state for reactivity)
      computedUser: null,
      computedUserEmail: null,
      computedUserName: null,

      // Temporary MFA state (will not be persisted)
      tempEmail: null,
      mfaInProgress: false,

      // Temporary PIN challenge state (login step-up)
      pinChallengeId: null,
      pinChallengeExpiresAt: null,
      pinRequired: false,

      // Computed user properties (derived from accessToken, not stored)
      get user() {
        const storeState = get();

        // Safety check: during rehydration, storeState might be undefined
        if (!storeState || !storeState.accessToken) {
          return null;
        }

        const token = storeState.accessToken;

        try {
          const parsedUser = parseJwt(token);
          return parsedUser;
        } catch (error) {
          console.error("Failed to parse JWT token:", error);
          return null;
        }
      },
      get userEmail() {
        const storeState = get();

        if (!storeState) {
          return null;
        }

        const user = storeState.user;
        const email = user?.sub || user?.email || user?.username || null;
        return email;
      },

      get userName() {
        const storeState = get();
        // Safety check: during rehydration, storeState might be undefined
        if (!storeState) return null;

        const user = storeState.user;
        // Based on your JWT: extract name from merchantName or email
        let name =
          user?.merchantName ||
          user?.name ||
          user?.firstName ||
          user?.given_name ||
          null;

        // If no name found, extract from email (sub field)
        if (!name && user?.sub) {
          name = extractDisplayNameFromEmail(user.sub);
        }

        return name;
      },

      get userRole() {
        const storeState = get();
        // Safety check: during rehydration, storeState might be undefined
        if (!storeState) return null;

        const user = storeState.user;
        return user?.role || null;
      },

      setAccessToken: (token) =>
        set({
          accessToken: token,
          isAuthenticated: !!token,
        }),

      setRefreshToken: (token) =>
        set({
          refreshToken: token,
        }),

      setTokens: (accessToken, refreshToken) => {
        const newState = {
          accessToken,
          refreshToken,
          isAuthenticated: !!accessToken,
        };

        // Store refresh token in secure cookie
        if (refreshToken) {
          setRefreshTokenCookie(refreshToken);
        }

        // If we have an access token, compute and store user data immediately
        if (accessToken) {
          try {
            const userData = parseJwt(accessToken);
            newState.computedUser = userData;
            newState.computedUserEmail =
              userData?.sub || userData?.email || null;
            let displayName = userData?.firstName || userData?.name || null;
            if (!displayName && userData?.sub) {
              displayName = extractDisplayNameFromEmail(userData.sub);
            }
            newState.computedUserName = displayName;
          } catch (error) {
            console.error("❌ Failed to compute user data:", error);
            newState.computedUser = null;
            newState.computedUserEmail = null;
            newState.computedUserName = null;
          }
        } else {
          newState.computedUser = null;
          newState.computedUserEmail = null;
          newState.computedUserName = null;
        }

        set(newState);
      },

      clearAuth: () => {
        // Clear refresh token cookie
        clearRefreshTokenCookie();

        set({
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          computedUser: null,
          computedUserEmail: null,
          computedUserName: null,
          tempEmail: null,
          mfaInProgress: false,
          pinChallengeId: null,
          pinChallengeExpiresAt: null,
          pinRequired: false,
        });
      },

      // Initialize computed user data from existing tokens (called after rehydration)
      initializeUserData: () => {
        const state = get();
        if (state.accessToken) {
          try {
            const userData = parseJwt(state.accessToken);

            let displayName = userData?.firstName || userData?.name || null;
            if (!displayName && userData?.sub) {
              displayName = extractDisplayNameFromEmail(userData.sub);
            }

            set({
              computedUser: userData,
              computedUserEmail: userData?.sub || userData?.email || null,
              computedUserName: displayName,
            });
          } catch (error) {
            console.error("❌ Failed to initialize user data:", error);
          }
        }
      },

      setLoading: (loading) => set({ isLoading: loading }),

      // Update computed user data from API response
      setComputedUser: (apiUser) => {
        if (!apiUser) return;

        try {
          let displayName =
            apiUser?.firstname || apiUser?.lastname
              ? `${apiUser.firstname || ""} ${apiUser.lastname || ""}`.trim()
              : apiUser?.name || null;

          if (!displayName && apiUser?.email) {
            displayName = extractDisplayNameFromEmail(apiUser.email);
          }

          set({
            computedUser: apiUser,
            computedUserEmail: apiUser?.email || null, // API uses 'email' field
            computedUserName: displayName,
          });
        } catch (error) {
          console.error("❌ Failed to update computed user data:", error);
        }
      },

      // MFA helpers
      setTempEmail: (email) => set({ tempEmail: email, mfaInProgress: true }),
      clearTempEmail: () => set({ tempEmail: null, mfaInProgress: false }),

      // PIN challenge helpers (login step-up)
      setPinChallenge: (challengeId, expiresAt) =>
        set({
          pinChallengeId: challengeId,
          pinChallengeExpiresAt: expiresAt,
          pinRequired: true,
        }),
      clearPinChallenge: () =>
        set({
          pinChallengeId: null,
          pinChallengeExpiresAt: null,
          pinRequired: false,
        }),
    }),
    {
      name: "auth-storage",

      // Only persist essential data - refresh tokens stored in secure cookies
      partialize: (state) => ({
        accessToken: state.accessToken,
        isAuthenticated: state.isAuthenticated,
        computedUser: state.computedUser,
        computedUserEmail: state.computedUserEmail,
        computedUserName: state.computedUserName,
        tempEmail: state.tempEmail,
        mfaInProgress: state.mfaInProgress,
        pinChallengeId: state.pinChallengeId,
        pinChallengeExpiresAt: state.pinChallengeExpiresAt,
        pinRequired: state.pinRequired,
        // refreshToken is excluded - stored in secure cookie instead
      }),

      onRehydrateStorage: () => {
        return (state, error) => {
          if (error) {
            return;
          }

          // After rehydration, get refresh token from cookie
          const refreshTokenFromCookie = getRefreshTokenCookie();
          if (refreshTokenFromCookie && state) {
            // Update the store with refresh token from cookie
            useAuthStore.setState((currentState) => ({
              ...currentState,
              refreshToken: refreshTokenFromCookie,
            }));
          }

          // Initialize computed user data after rehydration
          if (state?.accessToken) {
            setTimeout(() => {
              useAuthStore.getState().initializeUserData();
            }, 0);
          }
        };
      },

      merge: (persistedState, currentState) => {
        const merged = {
          ...currentState,
          ...persistedState,
        };

        return merged;
      },
    },
  ),
);

export default useAuthStore;
